"use client"

import { useFormik, FormikProvider } from "formik"
import { Loader2 } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormInput, FormSelect } from "@/components/form-inputs"
import { useUpdateAdminPartnerMutation } from "@/hooks/mutations/partners"
import { PARTNER_ACCOUNT_STATUSES } from "@/lib/partners/constants"
import { parsePercentInput } from "@/components/partners/client-bonus"
import { formatPercent } from "@/lib/partners/percent"
import type { PartnerAccountStatus } from "@/lib/partners/types"
import type { AdminPartnerCodeView, AdminPartnerView } from "@/server-actions/admin-partners"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





type EditPartnerForm = {
  commissionPercent: string
  status: PartnerAccountStatus
}

function getSTATUS_OPTIONS(tUi: ReturnType<typeof useTranslations<"Partners.PartnersAdminEditPartnerDialog">>): readonly { value: PartnerAccountStatus; label: string }[] {
  return [
  { value: PARTNER_ACCOUNT_STATUSES.ACTIVE, label: tUi("active") },
  { value: PARTNER_ACCOUNT_STATUSES.SUSPENDED, label: tUi("suspendedNoNewAttributionsNoAccrual") },
]
}

export function EditPartnerDialog({
  open,
  partner,
  codes,
  onOpenChange,
}: {
  open: boolean
  partner: AdminPartnerView
  codes: AdminPartnerCodeView[]
  onOpenChange: (open: boolean) => void
}) {


  const tUi = useTranslations("Partners.PartnersAdminEditPartnerDialog")
  const STATUS_OPTIONS = getSTATUS_OPTIONS(tUi)


  const mutation = useUpdateAdminPartnerMutation()

  const formik = useFormik<EditPartnerForm>({
    enableReinitialize: true,
    initialValues: {
      commissionPercent: partner.commission_percent_override === null ? "" : String(partner.commission_percent_override),
      status: partner.status,
    },
    validate: values => {
      const percent = parsePercentInput(values.commissionPercent)
      return percent === "invalid" ? { commissionPercent: tUi("enterAPercentageBetween0And100Or") } : {}
    },
    onSubmit: async values => {
      const percent = parsePercentInput(values.commissionPercent)
      if (percent === "invalid") return

      try {
        await mutation.mutateAsync({
          partner_id: partner.partner_id,
          commission_percent: percent,
          status: values.status,
        })
        onOpenChange(false)
      } catch {
        // withQueryHandling already surfaced the error.
      }
    },
  })

  const nextPercent = parsePercentInput(formik.values.commissionPercent)
  // An emptied field means "fall back to the platform default" — warning against the
  // partner's current percent would hide exactly the links that clearing it clamps.
  const clamped = nextPercent === "invalid"
    ? []
    : codesClampedBy(codes, nextPercent ?? partner.platform_default_percent)

  return (
    <Dialog open={open} onOpenChange={next => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DialogHeader>
              <DialogTitle><span key={tUi("editPartner")}>{tUi("editPartner")}</span></DialogTitle>
              <DialogDescription><TranslationText>{tUi.rich("value0PercentChangesApplyToFuturePaymentsOnly", { value0: String(partner.tenant_name) })}</TranslationText></DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-5">
              <FormInput
                name="commissionPercent"
                label={tUi("commission")}
                inputMode="decimal"
                placeholder={tUi("platformDefault")}
                description={tUi("leaveEmptyToFollowThePlatformDefaultA")}
              />
              <FormSelect<PartnerAccountStatus> name="status" label={tUi("status")} options={STATUS_OPTIONS} />

              {clamped.length > 0 && (
                <Alert variant="destructive">
                  <AlertTitle><span key={tUi("clientBonusesWillBeClamped")}>{tUi("clientBonusesWillBeClamped")}</span></AlertTitle>
                  <AlertDescription>
                    <p>
                      <span key={tUi("theseLinksPromiseTheClientMoreThanThe")}>{tUi("theseLinksPromiseTheClientMoreThanThe")}</span></p>
                    <ul className="mt-2 list-inside list-disc">
                      {clamped.map(code => (
                        <li key={code.id}><TranslationText>{tUi.rich("value0Value1Promised", { value0: String(code.label ?? code.code), value1: String(formatPercent(code.client_bonus_percent)) })}</TranslationText></li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={tUi("save")}>{tUi("save")}</span></Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Only links that inherit the partner's percent are affected — a link with its
 * own commission keeps clamping against that one.
 */
function codesClampedBy(codes: AdminPartnerCodeView[], percent: number): AdminPartnerCodeView[] {
  return codes.filter(code =>
    code.is_active
    && code.commission_percent_override === null
    && code.client_bonus_percent > percent)
}
